import { useSyncExternalStore } from "react";

/** Explicit user choice only; absent = follow the OS (`prefers-color-scheme`). */
const THEME_STORAGE_KEY = "agent-desk-theme-preference";
/** Former key, always written on load (so "light" there isn't a real choice; "dark" is). */
const LEGACY_THEME_STORAGE_KEY = "agent-desk-theme";
const DARK_SCHEME_QUERY = "(prefers-color-scheme: dark)";

export type Theme = "light" | "dark";

/** Applies the theme to `<body data-theme="...">`, which the Fluid design tokens'
 * `[data-theme="dark"]` CSS rules key off of (legacy parity: `setTheme.ts`). `color-scheme`
 * keeps native widgets (scrollbars, inputs) in sync. */
export function applyTheme(theme: Theme): void {
  document.body.setAttribute("data-theme", theme);
  document.documentElement.style.colorScheme = theme;
}

export function systemTheme(): Theme {
  return window.matchMedia(DARK_SCHEME_QUERY).matches ? "dark" : "light";
}

export function readStoredTheme(): Theme | null {
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return stored === "dark" || stored === "light" ? stored : null;
}

/** Applies a theme picked by the user. Picking the OS theme clears the override, so the app
 * follows the system again from then on. */
export function setThemePreference(theme: Theme): void {
  if (theme === systemTheme()) {
    window.localStorage.removeItem(THEME_STORAGE_KEY);
  } else {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  }
  applyTheme(theme);
}

/** Called once before the first render: applies the stored choice or the OS theme (no flash),
 * then tracks OS theme changes while the user hasn't overridden it. */
export function initTheme(): void {
  if (window.localStorage.getItem(LEGACY_THEME_STORAGE_KEY) === "dark") {
    window.localStorage.setItem(THEME_STORAGE_KEY, "dark");
  }
  window.localStorage.removeItem(LEGACY_THEME_STORAGE_KEY);

  applyTheme(readStoredTheme() ?? systemTheme());
  window.matchMedia(DARK_SCHEME_QUERY).addEventListener("change", () => {
    if (readStoredTheme() === null) applyTheme(systemTheme());
  });
}

function subscribeToTheme(callback: () => void): () => void {
  const observer = new MutationObserver(callback);
  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });
  return () => observer.disconnect();
}

/** Reactive read of the current dark-mode state (tracks `<body data-theme>` changes). */
export function useIsDarkMode(): boolean {
  return useSyncExternalStore(
    subscribeToTheme,
    () => document.body.getAttribute("data-theme") === "dark",
    () => false,
  );
}
